import { DateTime } from "luxon";

/**
 * Utility functions for SSE SQL database queries and parsing
 * Extracted from CaseOutput.vue for better separation of concerns
 */

/**
 * Converts SQL result columns and row into a JavaScript object
 * @param {Array} columns - Column names from SQL result
 * @param {Array} row - Row values from SQL result
 * @returns {Object} Parsed object with datetime handling
 */
export function sqlToObject(columns, row) {
  return columns.reduce((acc, key, index) => {
    const fixedKey = key === "date" ? "datetime" : key;
    let value = row[index];
    if (fixedKey === "datetime") {
      value = DateTime.fromISO(value.replace(" ", "T"), { zone: "UTC" }).toISO();
    }
    return { ...acc, [fixedKey]: value };
  }, {});
}

export async function parseSQLYearly(dbSql, servicableIDs) {
  const resourceTypes = [
    "markets",
    "storages",
    "demands",
    "thermal_generators",
    "renewable_generators",
  ];

  if (servicableIDs.length === 0) {
    return Object.fromEntries(resourceTypes.map((t) => [t, []]));
  }

  const parsed = {};
  const servicables = {};
  await Promise.all(
    resourceTypes.map(async (resource) => {
      const response = await dbSql.exec(
        `SELECT * FROM 'yearly_${resource}' WHERE servicableID IN (${servicableIDs.join(",")})`
      );
      if (!response[0]) {
        return;
      }
      const objectKeys = response[0].columns;
      response[0].values.forEach((row) => {
        const rowObject = sqlToObject(objectKeys, row);
        const { year, name: resourceName } = rowObject;
        if (!parsed[year]) {
          parsed[year] = {
            markets: {},
            storages: {},
            demands: {},
            thermal_generators: {},
            renewable_generators: {},
          };
        }
        if (!parsed[year][resource][resourceName]) {
          parsed[year][resource][resourceName] = rowObject;
        }
        servicables[rowObject.servicableID] = { type: resource, name: resourceName };
      });
    })
  );

  // Get yearly services
  const services = await dbSql.exec(
    `SELECT * FROM 'yearly_services' WHERE servicableID IN (${servicableIDs.join(",")})`
  );
  if (services[0]) {
    const objectKeys = services[0].columns;
    services[0].values.forEach((row) => {
      const rowObject = sqlToObject(objectKeys, row);
      const { servicableID, year, serviceName, subServiceName } = rowObject;
      const { type, name: resourceName } = servicables[servicableID];
      if (serviceName === "R") {
        if (!parsed[year][type][resourceName].R) {
          parsed[year][type][resourceName].R = {};
        }
        parsed[year][type][resourceName].R[subServiceName] = rowObject;
      } else {
        parsed[year][type][resourceName][serviceName] = rowObject;
      }
    });
  }
  return parsed;
}

// TODO: for yearly, datetime column doesn't exist, and output structure is a bit different
async function parseGeneric(dbSql, servicableIDs, dateCond, tablePrefix = "daily_") {
  const returnObj = {};
  const allResourceTypes = [
    "markets",
    "storages",
    "thermal_generators",
    "renewable_generators",
    "demands",
  ];
  const servicables = {};
  const dateIndices = {}; // 2020-02-01 will point to 1 if monthly, 31 if daily
  if (servicableIDs.length === 0) {
    return Object.fromEntries(allResourceTypes.map((t) => [t, []]));
  }

  await Promise.all(
    allResourceTypes.map(async (resource) => {
      const resResponse = await dbSql.exec(
        `SELECT * FROM '${tablePrefix}${resource}'
        WHERE servicableID IN (${servicableIDs.join(",")})
        AND ${dateCond}`
      );

      const parsedResources = {};
      if (!resResponse[0]) {
        returnObj[resource] = {};
        return;
      }
      const objectKeys = resResponse[0].columns;
      resResponse[0].values.forEach((row) => {
        const rowObject = sqlToObject(objectKeys, row);
        const { datetime, name: resourceName } = rowObject;
        const { year } = DateTime.fromISO(datetime, { zone: "UTC" });
        if (!parsedResources[resourceName]) {
          parsedResources[resourceName] = {
            [year]: [rowObject],
          };
        } else if (!parsedResources[resourceName][year]) {
          parsedResources[resourceName][year] = [rowObject];
        } else {
          parsedResources[resourceName][year].push(rowObject);
        }
        dateIndices[datetime] = parsedResources[resourceName][year].length - 1;
        servicables[rowObject.servicableID] = { type: resource, resourceName };
      });

      returnObj[resource] = parsedResources;
    })
  );
  const services = await dbSql.exec(
    `SELECT * FROM '${tablePrefix}services' 
    WHERE servicableID IN (${servicableIDs.join(",")})
    AND ${dateCond}`
  );
  if (services[0]) {
    const objectKeys = services[0].columns;
    services[0].values.forEach((row) => {
      const rowObject = sqlToObject(objectKeys, row);
      const { servicableID, serviceName, subServiceName, datetime } = rowObject;
      const { type, resourceName } = servicables[servicableID];
      const { year } = DateTime.fromISO(datetime, { zone: "UTC" });
      const idx = dateIndices[datetime];

      if (serviceName === "R") {
        if (!returnObj[type][resourceName][year][idx].R) {
          returnObj[type][resourceName][year][idx].R = {};
        }
        returnObj[type][resourceName][year][idx].R[subServiceName] = rowObject;
      } else {
        returnObj[type][resourceName][year][idx][serviceName] = rowObject;
      }
    });
  }
  return returnObj;
}

export async function parseTimeSeriesResources(projectObj, dbSql, resourceSelections, dates) {
  const [startDate, endDate] = dates;
  const nextDay = DateTime.fromISO(endDate.replace(" ", "T")).plus({ days: 1 }).toISODate();
  const dateCond = `datetime >= '${startDate}' AND datetime < '${nextDay}'`;

  const results = await parseGeneric(dbSql, resourceSelections, dateCond, "");
  return { ...projectObj, ...results };
}

export async function parseSQLDaily(projectObj, dbSql, resourceSelections, selectedYears) {
  const projectCopy = projectObj;
  if (selectedYears.length === 0) {
    projectCopy.dailyResults = {};
    return projectCopy;
  }
  const dateCond = selectedYears.map((y) => `datetime LIKE '${y}-%'`).join(" OR ");
  projectCopy.dailyResults = await parseGeneric(
    dbSql,
    resourceSelections,
    `(${dateCond})`,
    "daily_"
  );
  return projectCopy;
}

export async function parseSQLMonthly(projectObj, dbSql, resourceSelections, selectedYears) {
  const projectCopy = projectObj;
  if (selectedYears.length === 0) {
    projectCopy.monthlyResults = {};
    return projectCopy;
  }
  const dateCond = selectedYears.map((y) => `datetime LIKE '${y}-%'`).join(" OR ");
  projectCopy.monthlyResults = await parseGeneric(
    dbSql,
    resourceSelections,
    `(${dateCond})`,
    "monthly_"
  );
  return projectCopy;
}
